import React, { useState } from "react";
import { MapPin } from "lucide-react";

// Real Côte d'Ivoire outline map
const MAP_IMG =
  "https://customer-assets.emergentagent.com/job_vision-landing-7/artifacts/ifglh8bd_IMG_0498.gif";

// Coverage areas - cities where Mikassa Agency clients operate
const POLES = [
  { id: "korhogo",     name: "Korhogo",               region: "Nord",              units: "15+",    hectares: "Agences", signature: "Expansion en cours",              social: "Actif", x: 43, y: 22 },
  { id: "bouake",      name: "Bouaké",                region: "Centre",            units: "45+",    hectares: "Agences", signature: "2ème ville du pays",              social: "Actif", x: 53, y: 45 },
  { id: "yamoussoukro",name: "Yamoussoukro",          region: "Centre",            units: "30+",    hectares: "Agences", signature: "Capitale politique",              social: "Actif", x: 53, y: 56 },
  { id: "sanpedro",    name: "San-Pedro",             region: "Sud-Ouest",         units: "25+",    hectares: "Agences", signature: "Hub portuaire",                   social: "Actif", x: 28, y: 87 },
  { id: "cocody",      name: "Cocody",                region: "Abidjan",           units: "150+",   hectares: "Agences", signature: "Quartier résidentiel premium",    social: "Actif", x: 66, y: 82 },
  { id: "plateau",     name: "Plateau",               region: "Abidjan",           units: "80+",    hectares: "Agences", signature: "Centre des affaires",             social: "Actif", x: 64, y: 83 },
  { id: "marcory",     name: "Marcory",               region: "Abidjan",           units: "120+",   hectares: "Agences", signature: "Zone commerciale",                social: "Actif", x: 65, y: 84 },
  { id: "yopougon",    name: "Yopougon",              region: "Abidjan",           units: "200+",   hectares: "Agences", signature: "Plus grande commune",             social: "Actif", x: 58, y: 83 },
];

const PolesMap = () => {
  const [active, setActive] = useState(POLES[4]); // Cocody as default

  return (
    <section
      id="couverture"
      data-testid="poles-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 border-t border-[#0C2340]/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        {/* <div className="flex items-center gap-3 mb-8">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            Notre couverture nationale
          </span>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16">
          <h2
            data-testid="poles-title"
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Présent dans toute<br />
            <span className="text-[#E8611A]">la Côte d'Ivoire.</span>
          </h2>
          <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Des agences et gestionnaires nous font confiance partout dans le pays —
            d'Abidjan aux grandes villes de l'intérieur. Cliquez sur une ville
            pour voir les détails.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* Map */}
          <div className="lg:col-span-7">
            <div className="relative aspect-square bg-[#0A1C33] overflow-hidden">
              {/* Grid backdrop */}
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.10]"
                style={{
                  backgroundImage:
                    "linear-gradient(#FAFAF8 1px, transparent 1px), linear-gradient(90deg, #FAFAF8 1px, transparent 1px)",
                  backgroundSize: "42px 42px",
                }}
              />
              {/* Diamond decor */}
              <div className="absolute -top-16 -right-16 w-56 h-56 rotate-45 border border-white/10" />
              <div className="absolute -bottom-20 -left-20 w-48 h-48 rotate-45 border border-[#E8611A]/25" />

              {/* Base map image (inverted + screen blend so black outlines
                  render as clean white lines on the navy background) */}
              <div className="absolute inset-0 flex items-center justify-center p-6 md:p-10">
                <div className="relative w-full h-full">
                  <img
                    src={MAP_IMG}
                    alt="Carte de la Côte d'Ivoire — couverture Mikassa Agency"
                    loading="lazy"
                    className="w-full h-full object-contain select-none"
                    style={{
                      filter: "invert(1) contrast(1.15) brightness(1.05)",
                      mixBlendMode: "screen",
                      opacity: 0.9,
                    }}
                    draggable={false}
                  />
                  {/* Markers overlay — positioned relative to the same aspect-ratio wrapper */}
                  <div className="absolute inset-0">
                    {POLES.map((p) => {
                      const isActive = p.id === active.id;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setActive(p)}
                          data-testid={`pole-marker-${p.id}`}
                          className="absolute -translate-x-1/2 -translate-y-1/2 group"
                          style={{ left: `${p.x}%`, top: `${p.y}%` }}
                          aria-label={p.name}
                        >
                          {isActive && (
                            <>
                              <span className="absolute inset-0 -m-3 rounded-full bg-[#E8611A]/25 animate-ping" />
                              <span className="absolute inset-0 -m-1 rounded-full bg-[#E8611A]/40" />
                            </>
                          )}
                          <span
                            className={`relative block w-3 h-3 md:w-3.5 md:h-3.5 rotate-45 transition-all duration-300 ${
                              isActive
                                ? "bg-[#E8611A] shadow-[0_0_0_2px_#FAFAF8]"
                                : "bg-[#FAFAF8] group-hover:bg-[#E8611A] shadow-[0_0_0_1.5px_#E8611A]"
                            }`}
                          />
                          <span
                            className={`absolute left-1/2 -translate-x-1/2 top-4 md:top-5 whitespace-nowrap font-body text-[0.55rem] md:text-[0.65rem] uppercase tracking-[0.2em] transition-opacity ${
                              isActive
                                ? "text-white opacity-100"
                                : "text-white/60 opacity-0 group-hover:opacity-100"
                            }`}
                          >
                            {p.name.split(/[-\s]/)[0]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="absolute bottom-6 left-6 flex items-center gap-3 text-white/70">
                <div className="w-2 h-2 rotate-45 bg-[#E8611A]" />
                <span className="font-body text-[0.6rem] uppercase tracking-[0.28em]">
                  Zone couverte
                </span>
              </div>
              <div className="absolute top-6 right-6 font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50">
                Côte d'Ivoire · {POLES.length} villes
              </div>
              {/* Compass */}
              <div className="absolute top-6 left-6 flex flex-col items-center gap-1 text-white/50">
                <span className="font-heading font-black text-xs">N</span>
                <div className="w-0.5 h-6 bg-gradient-to-b from-[#E8611A] to-transparent" />
              </div>
            </div>

            {/* Pole list */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
              {POLES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActive(p)}
                  data-testid={`pole-btn-${p.id}`}
                  className={`text-left px-3 py-2 border-l-2 transition-all ${
                    active.id === p.id
                      ? "border-[#E8611A] bg-[#0C2340]/[0.03]"
                      : "border-[#0C2340]/15 hover:border-[#E8611A]/50"
                  }`}
                >
                  <div className="font-heading font-bold text-[#0C2340] text-xs md:text-sm truncate">
                    {p.name}
                  </div>
                  <div className="font-body text-[#636E72] text-[0.6rem] uppercase tracking-[0.18em]">
                    {p.units}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail panel */}
          <div
            key={active.id}
            className="lg:col-span-5 bg-[#0C2340] text-white p-8 md:p-12 relative animate-fade-up"
            data-testid="pole-detail"
          >
            <div className="flex items-center gap-3 mb-6 text-white/60">
              <MapPin size={16} strokeWidth={1.4} className="text-[#E8611A]" />
              <span className="font-body text-[0.65rem] uppercase tracking-[0.28em]">
                {active.region}
              </span>
            </div>
            <h3 className="font-heading font-black text-white text-3xl md:text-5xl tracking-tighter leading-none mb-4" data-testid="pole-detail-name">
              {active.name}
            </h3>
            <p className="font-body text-[#E8611A] text-sm md:text-base mb-10 uppercase tracking-[0.14em]">
              {active.signature}
            </p>

            <div className="grid grid-cols-2 gap-y-8 border-t border-white/12 pt-8">
              <div>
                <div className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
                  {active.units}
                </div>
                <div className="font-body text-white/60 text-[0.65rem] uppercase tracking-[0.24em] mt-1">
                  Biens gérés
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
                  {active.hectares}
                </div>
                <div className="font-body text-white/60 text-[0.65rem] uppercase tracking-[0.24em] mt-1">
                  Clients
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
                  {active.social}
                </div>
                <div className="font-body text-white/60 text-[0.65rem] uppercase tracking-[0.24em] mt-1">
                  Statut
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
                  24/7<span className="text-[#E8611A]">·</span>
                </div>
                <div className="font-body text-white/60 text-[0.65rem] uppercase tracking-[0.24em] mt-1">
                  Support
                </div>
              </div>
            </div>

            <a
              href="#contact"
              className="mt-10 mikassa-btn-primary w-full justify-center"
              data-testid="pole-detail-cta"
            >
              Rejoindre nos clients
              <span className="mikassa-diamond" style={{ background: "#FAFAF8" }} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PolesMap;
